<template>
  <div class="chart-container d-flex justify-center align-center flex-column w-100">
    <p class="list__header mx-auto" :class="mdAndDown ? 'mx-auto' : 'ml-0 mr-auto'">
      {{ title }}
    </p>

    <div style="width: 100%;" class="d-flex align-center justify-center flex-wrap">
      <v-menu
        ref="startDate"
        v-model="startDateMenu"
        :close-on-content-click="false"
        :return-value.sync="startDate"
        transition="scale-transition"
        offset-y
        max-width="290px"
        min-width="290px"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            class="chart-input"
            v-model="startDate"
            label="Fecha inicio"
            prepend-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="startDate"
          locale="es"
          scrollable
        >
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="startDateMenu = false">Cancelar</v-btn>
          <v-btn text color="primary" @click="$refs.startDate.save(startDate); fetchData()">OK</v-btn>
        </v-date-picker>
      </v-menu>

      <v-menu
        ref="endDate"
        v-model="endDateMenu"
        :close-on-content-click="false"
        :return-value.sync="endDate"
        transition="scale-transition"
        offset-y
        max-width="290px"
        min-width="290px"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            class="chart-input mx-4"
            v-model="endDate"
            label="Fecha fin"
            prepend-icon="mdi-calendar"
            readonly
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="endDate"
          locale="es"
          scrollable
        >
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="endDateMenu = false">Cancelar</v-btn>
          <v-btn text color="primary" @click="$refs.endDate.save(endDate); fetchData()">OK</v-btn>
        </v-date-picker>
      </v-menu>

      <v-select
        v-model="selectedSeccion"
        :items="secciones"
        item-text="nombre"
        item-value="id"
        label="Sección"
        clearable
        dense
        outlined
        hide-details
        style="max-width: 200px"
        @change="fetchData"
      />
    </div>

    <!-- Resumen -->
    <p v-if="startDate && endDate" class="amount-summary mt-2">
      Estudiantes por edad entre {{ startDate | moment('DD/MM/YYYY') }} y {{ endDate | moment('DD/MM/YYYY') }}
    </p>

    <ApexChart
      :key="chartKey"
      ref="chartRef"
      width="180%"
      height="240"
      :type="chartType"           
      :options="safeOptions"
      :series="series"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { settingsBars } from '@/utils/graphs/edades'

export default {
  props: {
    title: { type: String, default: 'Estudiantes por edad' },
    globalCategories: { type: Array, default: () => [] },
    globalSections:   { type: Array, default: () => [] },
    globalCategory: { type: String, default: null },
    globalSection: { type: [Number, String], default: null },
    globalStartDate: { type: String, default: null },
    globalEndDate: { type: String, default: null },
    includeInactive: { type: Boolean, default: false }

  },
  data() {
    const baseOpts = JSON.parse(JSON.stringify(settingsBars.options))
    baseOpts.xaxis = {
      ...(baseOpts.xaxis || {}),
      title: { text: 'Edad' },
      categories: []
    }
    return {
      startDate: this.globalStartDate,
      endDate: this.globalEndDate,
      selectedSeccion: null,
      secciones: [],
      startDateMenu: false,
      endDateMenu: false,
      chartKey: 0,
      chartType: 'line', // 'scatter' 'line' 'bar'
      series: [{ name: 'Alumnos', data: [] }],
      options: baseOpts,
      fetchTimer: null, 
    }
  },
  filters: {
  moment (v, fmt) {
    if (!v) return ''
    const [yyyy, mm, dd] = String(v).split('-')
    return fmt === 'DD/MM/YYYY' ? `${dd}/${mm}/${yyyy}` : v
  }
  },
  computed: {
    mdAndDown() {
      return this.$vuetify.breakpoint.mdAndDown
    },
      safeOptions() {
    return JSON.parse(JSON.stringify(this.options))
    }
  },
  watch: {
    globalSections   () { this.fetchData() },
    globalCategories () { this.fetchData() },
    globalSection (v)   { this.selectedSeccion = v; this.fetchData() },
    globalStartDate (v) { this.startDate = v; this.fetchData() },
    globalEndDate (v)   { this.endDate = v; this.fetchData() },
    globalCategory ()   { this.fetchData() },
  },
  methods: {
    buildParams () {
      const p = new URLSearchParams()
      const fi = this.startDate || this.globalStartDate
      const ff = this.endDate   || this.globalEndDate
      if (fi) p.set('fecha_inicio', fi)
      if (ff) p.set('fecha_fin',   ff)
      if (this.includeInactive) p.set('include_inactive', '1')
      if (this.selectedSeccion) {
        p.append('seccion', this.selectedSeccion)
        return p
      }
      const secs = Array.isArray(this.globalSections) && this.globalSections.length
        ? this.globalSections
        : (this.globalSection ? [this.globalSection] : [])

      const cats = Array.isArray(this.globalCategories) && this.globalCategories.length
        ? this.globalCategories
        : (this.globalCategory ? [this.globalCategory] : [])

      if (secs.length) {
        secs.filter(Boolean).forEach(id => p.append('seccion', id))
      } else if (cats.length) {
        cats.filter(Boolean).forEach(slug => p.append('categoria', slug))
      }
      return p
    },
    async fetchData () {
      clearTimeout(this.fetchTimer)
      this.fetchTimer = setTimeout(async () => {
        try {
          const params = this.buildParams()
          const { data } = await axios.get(`api/admin/graficas/cantidad_estudiantes_por_edad/?${params.toString()}`)

          const labels = Array.isArray(data.labels) ? data.labels : []
          const values = (Array.isArray(data.series) ? data.series : []).map(Number)

          this.options = {
            ...this.options,
            xaxis: {
              ...(this.options.xaxis || {}),
              type: 'category',
              categories: labels,
              tickPlacement: 'on',
              labels: { rotate: -45, trim: true, hideOverlappingLabels: true }
            },
            tooltip: { x: { show: true } },
            plotOptions: this.chartType === 'bar' ? { bar: { columnWidth: '60%' } } : undefined,
            stroke: this.chartType !== 'bar' ? { width: 2 } : undefined,
            markers: this.chartType !== 'bar' ? { size: 3 } : undefined
          }
          this.series = [{ name: 'Alumnos', data: values }]

          this.chartKey += 1
          this.$nextTick(() => {
            this.$refs.chartRef?.updateOptions?.(this.safeOptions, false, true, true)
            this.$refs.chartRef?.updateSeries?.(this.series, true)
          })
        } catch (e) {
          console.error('Error al obtener datos de edades:', e)
        }
      }, 120)
    },
    async fetchSecciones () {
      const { data } = await axios.get('api/matriculas/secciones/')
      this.secciones = data
    },
    clearFilters() {
      this.startDate = this.globalStartDate
      this.endDate = this.globalEndDate
      this.selectedSeccion = null
      this.fetchData()
    }
  },
  async created() {
    await this.fetchSecciones()
    await this.fetchData()
  }
}
</script>

<style scoped>
.chart-container {
  background-color: #fff;
  padding: 20px;
  border-radius: 15px;
  width: 100%;
}
.chart-input {
  max-width: 180px;
}
.amount-summary {
  font-size: 14px;
  color: #455a64;
}
</style>